import { useEffect } from 'react';
import urltemplate from 'url-template';
import type { DateTime, Uuid } from 'src/apis/api-utilities';
import type { CapacityCategory } from 'src/apis/monolith/types';
import type {
  BalancingGroupsParams,
  BalancingGroupsResponse,
} from 'src/apis/transport-auction/types';
import type { OptionalTransportAuctionServiceRootRequest } from 'src/apis/transport-auction/use-transport-auction-service-root';
import { useAxios } from 'src/hooks/use-axios';

export type BalancingGroupsHookParams = {
  networkPointId: Uuid;
  capacityCategoryId: CapacityCategory;
  runtimeStart: DateTime;
  runtimeEnd: DateTime;
};

type HookOptions = {
  root: OptionalTransportAuctionServiceRootRequest;
  params: BalancingGroupsHookParams;
  /**
   * Bundled network points are currently ignored as we do not have the UUID per side
   * in the old API payload.
   */
  bundled: boolean;
};

type ExecuteOptions = {
  url: string;
  params: BalancingGroupsParams;
};

export function useAssignedBalancingGroups({
  params,
  root,
  bundled,
}: HookOptions) {
  const portfolioCodes = useAxios(
    (axios, baseConfig, { url, params }: ExecuteOptions) =>
      axios.request<BalancingGroupsResponse>({
        ...baseConfig,
        // use templated urls here instead of using the params field,
        // as they are marked as required params in the URL template
        url: urltemplate.parse(url).expand(params),
        // params,
      }),
    { neededOnPageLoad: true }
  );

  const assignedBalancingGroupsLink =
    root?.response?.data._links.assignedBalancingGroups;

  useEffect(() => {
    if (!assignedBalancingGroupsLink) return;
    if (bundled) return;

    portfolioCodes.execute({
      url: assignedBalancingGroupsLink.href,
      params: {
        ...params,
        capacityCategoryId: capacityCategories[params.capacityCategoryId],
      },
    });
    // oxlint-disable-next-line exhaustive-deps
  }, [assignedBalancingGroupsLink, bundled, params]);

  if (!assignedBalancingGroupsLink) return;
  if (bundled) return;

  return portfolioCodes;
}

export type AssignedBalancingGroupsRequest = ReturnType<
  typeof useAssignedBalancingGroups
>;

// we temporarily map these capacity categories to their UUIDs in the FE
const capacityCategories: Record<CapacityCategory, Uuid> = {
  FZK: '8ed4e2e4-9e81-11ea-bb37-0242ac130002',
  FZK_C: '8ed4e62c-9e81-11ea-bb37-0242ac130002',
  FZK_INC: '8ed4e730-9e81-11ea-bb37-0242ac130002',
  B_FZK: '8ed4e802-9e81-11ea-bb37-0242ac130002',
  B_FZK_C: '40c16ce4-6069-11eb-ae71-db5f009637fe',
  B_FZK_1: '8ed4e8c0-9e81-11ea-bb37-0242ac130002',
  BZK: '8ed4e97e-9e81-11ea-bb37-0242ac130002',
  U_BZK: '8ed4ea46-9e81-11ea-bb37-0242ac130002',
  U_KC: '8ed4eb04-9e81-11ea-bb37-0242ac130002',
  DZK: '8ed4ef1e-9e81-11ea-bb37-0242ac130002',
  TVK: '8ed4eff0-9e81-11ea-bb37-0242ac130002',
  FIRM: '8ed4f0ae-9e81-11ea-bb37-0242ac130002',
  FIRM_ADDITIONAL: '8ed4f176-9e81-11ea-bb37-0242ac130002',
  INTERRUPTIBLE: '8ed4f2b6-9e81-11ea-bb37-0242ac130002',
  INTERRUPTIBLE_LEVEL_1: '8ed4f4f0-9e81-11ea-bb37-0242ac130002',
  INTERRUPTIBLE_LEVEL_2: '8ed4f5fe-9e81-11ea-bb37-0242ac130002',
  INTERRUPTIBLE_LEVEL_N: '8ed4f6bc-9e81-11ea-bb37-0242ac130002',
  BACKHAUL_CAPACITY: '8ed4f77a-9e81-11ea-bb37-0242ac130002',
  BACKHAUL_LEVEL_1: '8ed4f82e-9e81-11ea-bb37-0242ac130002',
  BACKHAUL_LEVEL_2: '8ed4f8ec-9e81-11ea-bb37-0242ac130002',
  BACKHAUL_LEVEL_N: '8ed4faa4-9e81-11ea-bb37-0242ac130002',
  DZK_1: '8ed4fb6c-9e81-11ea-bb37-0242ac130002',
  DZK_2: '8ed4fc2a-9e81-11ea-bb37-0242ac130002',
  DZK_3: '8ed4fce8-9e81-11ea-bb37-0242ac130002',
  DZK_4: '8ed4fd9c-9e81-11ea-bb37-0242ac130002',
  DZK_5: '8ed4fe5a-9e81-11ea-bb37-0242ac130002',
  INCREMENTAL_OFFER_LEVEL_1: '8ed50026-9e81-11ea-bb37-0242ac130002',
  INCREMENTAL_OFFER_LEVEL_2: '8ed500ee-9e81-11ea-bb37-0242ac130002',
  INCREMENTAL_OFFER_LEVEL_3: '8ed501ac-9e81-11ea-bb37-0242ac130002',
  INCREMENTAL_OFFER_LEVEL_4: '8ed502e2-9e81-11ea-bb37-0242ac130002',
  INCREMENTAL_OFFER_LEVEL_5: '8ed503be-9e81-11ea-bb37-0242ac130002',
  DZK_SH: '50a3effa-9e82-11ea-bb37-0242ac130002',
  IUKCF_1: '50a3f3c4-9e82-11ea-bb37-0242ac130002',
};
